/**
 * Beacon Golf — per-route head metadata (BEACON-55 D.1)
 *
 * The PWA is a single HTML shell whose route is encoded in `?course=<slug>`.
 * For share-edge previews (iMessage, Telegram, Twitter, Slack, LinkedIn) and
 * SEO, we want each course route to advertise its own `<title>`, OG, and
 * Twitter card. This module is applied on every page load (including reloads
 * triggered by `CoursePicker → window.location.assign`); the app does not
 * currently maintain SPA history state, so no `popstate` hook is required.
 *
 * CCQ-430 R1 L-3 / I-1 fold: the prior docstring overstated the trigger
 * model. Today, course switches are full reloads (CoursePicker calls
 * `window.location.assign(url)`) — boot() re-runs and head meta is re-applied.
 * If any future feature adds `history.pushState` / `history.replaceState`
 * (e.g. tab routing inside the brief panel, hole deep links via fragment +
 * pushState, in-place course switching to avoid the 80ms reload jank), you
 * MUST call `updateHeadForCourse(course, slug)` AND `recordLastViewed(slug,
 * name)` from the popstate / route-change handler. This module already
 * exposes those entry points cleanly.
 *
 * Static fallback meta (when `?course=` is absent or invalid) lives in
 * `pwa/index.html` so crawlers that don't execute JS still get sane defaults.
 * The DEFAULT_DESCRIPTION constant below is the single source of truth shared
 * with the static block in `index.html`.
 *
 * Build-time prerequisite:
 *   `pwa/scripts/build-og-images.mjs` generates `/og/<slug>.jpg` (1200×630)
 *   for each course. This module references those URLs by convention.
 */

const ABSOLUTE_BASE =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  "https://beacon-golf.pages.dev";

// CCQ-430 R1 I-2 fold: ABSOLUTE_BASE matrix for canonical/og:url/og:image:
//   - localhost dev → canonical/og:url rewritten to prod, og:image too
//     (so share-edge bots that hit a localhost share-link still resolve)
//   - <sha>.beacon-golf.pages.dev preview → canonical/og:url stay on preview
//     origin (preview-deployment QA wants previewers to see preview meta);
//     og:image stays on preview origin too so freshly-built OG cards are
//     visible to the previewer
//   - prod beacon-golf.pages.dev → all absolute URLs use prod
const PROD_BASE = "https://beacon-golf.pages.dev";
function productionBase() {
  // Localhost is the only environment we rewrite to prod; everything else
  // (preview deployments, prod) keeps its origin so og:image and canonical
  // stay coherent for whichever audience is viewing the page.
  return ABSOLUTE_BASE.includes("localhost") ? PROD_BASE : ABSOLUTE_BASE;
}

// CCQ-430 R1 L-2 fold: single source of truth for the no-JS / no-slug
// description. Mirrored verbatim into pwa/index.html static <meta> block;
// when this changes, update both files together (CI grep enforces parity).
const DEFAULT_DESCRIPTION =
  "RF's caddy — course briefs, conditions selector, hole-by-hole notes for Crandon and the Bandon Dunes resort.";

const SITE_NAME = "Beacon Golf";
const TWITTER_HANDLE = "@rfd100";

/**
 * Get-or-create a head element matching the selector. We use `querySelector`
 * with the canonical attribute (e.g. `meta[property="og:title"]`) and create
 * the element if missing so we can safely call this on the first load.
 */
function ensureMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k === "tag") return;
      el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Serialize a course `location.json` shape into a one-line description
 * suitable for `<meta name="description">` and `og:description`. Falls back
 * to a generic site description when course data isn't available.
 */
function describeCourse(course) {
  if (!course) return DEFAULT_DESCRIPTION;
  const parts = [];
  if (course.name) parts.push(course.name);
  if (course.holes) parts.push(`${course.holes} holes`);
  if (course.address) {
    // Strip the long mailing address; keep just the city/state if recognizable
    const m = course.address.match(/,\s*([A-Z][a-z]+(?:\s[A-Z][a-z]+)*),\s*[A-Z]{2}\s*\d/);
    if (m) parts.push(m[1]);
  }
  if (course.prevailing_wind_summary) {
    // First sentence only — the wind summaries are 2-3 sentences in source
    const first = course.prevailing_wind_summary.split(/\.\s+/)[0];
    if (first && first.length < 110) parts.push(first.endsWith(".") ? first : first + ".");
  }
  return parts.join(" · ").slice(0, 200);
}

/**
 * Compute the absolute share-edge URL for the current route. Anchored to the
 * production domain so previews scraped from a deployed PR still resolve.
 */
function buildShareUrl(slug) {
  const base = productionBase();
  // CCQ-430 R1 L-3 fold: encodeURIComponent the slug. SLUG_RE in main.js
  // already whitelists slugs to /^[a-z0-9]+(-[a-z0-9]+)*$/ before they
  // reach here, so this is defense-in-depth for any future caller that
  // bypasses the whitelist. Mirrors the encoding in offline.html:236.
  return slug ? `${base}/?course=${encodeURIComponent(slug)}` : base;
}

/**
 * Update `<head>` with course-specific metadata. Idempotent: re-running with
 * the same course produces no churn.
 *
 * @param {object} course - parsed `location.json` for the active course; may
 *                          be null/undefined when the slug is invalid
 * @param {string} slug   - the resolved course slug (used for OG image URL)
 */
export function updateHeadForCourse(course, slug) {
  const courseName = course?.name || SITE_NAME;
  const title = course ? `${courseName} · ${SITE_NAME}` : SITE_NAME;
  const description = describeCourse(course);
  const shareUrl = buildShareUrl(slug);
  // CCQ-430 R1 L-1 fold: og:image was leaking localhost in dev. Use the
  // same productionBase() helper so canonical, og:url, AND og:image all
  // resolve to a real share-edge URL.
  const imageBase = productionBase();
  const ogImage = slug ? `${imageBase}/og/${slug}.jpg` : `${imageBase}/og/default.jpg`;

  // <title>
  if (document.title !== title) document.title = title;

  // <meta name="description">
  ensureMeta('meta[name="description"]', { tag: "meta", name: "description" })
    .setAttribute("content", description);

  // <link rel="canonical">
  const canonical = ensureMeta('link[rel="canonical"]', { tag: "link", rel: "canonical" });
  canonical.setAttribute("href", shareUrl);

  // Open Graph
  const og = [
    ["og:title", title],
    ["og:description", description],
    ["og:type", "website"],
    ["og:url", shareUrl],
    ["og:image", ogImage],
    ["og:image:width", "1200"],
    ["og:image:height", "630"],
    ["og:image:alt", `${courseName} — Beacon Golf brief`],
    ["og:site_name", SITE_NAME],
    ["og:locale", "en_US"],
  ];
  for (const [property, content] of og) {
    ensureMeta(`meta[property="${property}"]`, { tag: "meta", property })
      .setAttribute("content", content);
  }

  // Twitter card (summary_large_image so the OG image renders prominently)
  const tw = [
    ["twitter:card", "summary_large_image"],
    ["twitter:title", title],
    ["twitter:description", description],
    ["twitter:image", ogImage],
    ["twitter:image:alt", `${courseName} — Beacon Golf brief`],
    ["twitter:creator", TWITTER_HANDLE],
    ["twitter:site", TWITTER_HANDLE],
  ];
  for (const [name, content] of tw) {
    ensureMeta(`meta[name="${name}"]`, { tag: "meta", name })
      .setAttribute("content", content);
  }
}

/**
 * Reset head to the static fallback (used when slug is invalid and we want
 * the share-edge to advertise the site in general, not a phantom course).
 */
export function resetHeadToDefault() {
  updateHeadForCourse(null, null);
}
